import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useTranslation } from "react-i18next";

const About = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen pt-16">
{/* Hero Section */}
<section className="py-20 bg-black text-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center max-w-4xl mx-auto">
     
        <a
  href="https://docs.google.com/document/d/1wO92HJU6tY7f6XKDf74GVCZCSWPVujn-x1XTBZTM9eE/edit?usp=sharing"
  target="_blank"
  rel="noopener noreferrer"
>
  <Badge className="mb-6 bg-yellow-600 text-black px-4 py-2 text-sm font-medium cursor-pointer hover:bg-yellow-500 transition">
    {t("about.hero.badge")}
  </Badge>
</a>

        
      
      <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white leading-tight">
        {t("about.hero.title")}{" "}
        <span className="text-yellow-400">{t("about.hero.highlight")}</span>
      </h1>
      <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
        {t("about.hero.subtitle")}
      </p>
    </div>
  </div>
</section>

{/* Mission & Vision */}
<section className="py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <h2 className="text-4xl font-bold text-black mb-6">
          {t("about.mission.title")}
        </h2>
        <p className="text-lg text-gray-600 mb-6 leading-relaxed">
          {t("about.mission.description")}
        </p>
        <div className="space-y-4">
          {mockData.mission.values.map((item, idx) => (
            <div key={idx} className="flex items-start space-x-3">
              <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-black">{t(item.titleKey)}</h3>
                <p className="text-gray-600">{t(item.descKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Impact */}
      <div className="relative">
        <div className="bg-black rounded-2xl p-8 text-white">
          <h3 className="text-2xl font-bold mb-4 text-yellow-400">
            {t("about.impact.title")}
          </h3>
          <div className="grid grid-cols-2 gap-6">
            {mockData.impact.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-3xl font-bold mb-2 text-yellow-400">
                  {stat.value}
                </div>
                <div className="text-gray-300">{t(stat.labelKey)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

      {/* Programs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
<div className="text-center mb-16">
  <h2 className="text-4xl font-bold text-black mb-4">
    {t("about.programs.title")}
  </h2>
  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
    {t("about.programs.subtitle")}
  </p>
</div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
{mockData.programs.map((program, index) => (
  <Card
    key={index}
    className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-200 h-full"
  >
    <CardHeader className="pb-4">
      <div className="flex items-center justify-between mb-2">
        <CardTitle className="text-xl font-bold text-black">
          {t(program.titleKey)}
        </CardTitle>
      </div>
      <CardDescription className="text-gray-600 leading-relaxed">
        {t(program.descriptionKey)}
      </CardDescription>
      
    </CardHeader>

    <CardContent className="flex-1">
      <h4 className="font-semibold text-black mb-3">
        {t("about.programs.benefitsTitle")}
      </h4>
      <ul className="space-y-2 mb-6">
        {program.benefitsKeys &&
          program.benefitsKeys.map((benefitKey, idx) => (
            <li key={idx} className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
              <span className="text-gray-600 text-sm">{t(benefitKey)}</span>
            </li>
          ))}
      </ul>

      <div className="mt-auto">
        <p className="text-sm text-gray-500 mb-4">
          <strong>{t("about.programs.eligibility")}:</strong>{" "}
          {t(program.eligibilityKey)}
        </p>
      </div>

    </CardContent>
  </Card>
))}

          </div>
        </div>
      </section>



      {/* team */}
<section className="py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-16">
      <h2 className="text-4xl font-bold text-black mb-4">{t("about.team.title")}</h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("about.team.subtitle")}</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      {mockData.team.map((member, index) => (
        <Card
          key={index}
          className="group hover:shadow-xl transition-all duration-300 border border-gray-200 text-center"
        >
          <CardHeader className="pb-4">
            {member.image ? (
              <img
                src={member.image}
                alt={member.name}
                className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
              />
            ) : (
              <div className="w-24 h-24 bg-yellow-600 rounded-full flex items-center justify-center text-black font-bold text-2xl mx-auto mb-4">
                {member.name.split(" ").map((n) => n[0]).join("")}
              </div>
            )}
            <CardTitle className="text-lg font-bold text-black">
              {member.name}
            </CardTitle>
            <CardDescription className="text-yellow-600 font-medium">
              {t(member.roleKey)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 text-sm mb-4">{t(member.bioKey)}</p>
            <div className="flex flex-wrap gap-1 justify-center">
              {member.expertiseKeys.map((skill, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">
                  {t(skill)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  </div>
</section>



      {/* Application Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-4">{t("about.application.title")}</h2>
            <p className="text-xl text-gray-600">{t("about.application.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.applicationProcess.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center text-yellow-400 font-bold text-xl mx-auto mb-4">
                  {step.step}
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{t(step.titleKey)}</h3>
                <p className="text-gray-600">{t(step.descriptionKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black text-white text-center">
        <h2 className="text-4xl font-bold mb-6">{t("about.cta.title")}</h2>
        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">{t("about.cta.subtitle")}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold px-8 py-4 rounded-full">
            <Link to="/register">{t("about.cta.apply")} <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black">
            <Link to="/contact">{t("about.cta.contact")}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
